import React, { createContext, useContext, useEffect } from 'react';
import { useIsFutureEnabled } from '../hooks/useIsFeatureEnabled';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  highContrast: boolean;
  toggleHighContrast: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  toggleDarkMode: () => {},
  highContrast: false,
  toggleHighContrast: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = React.useState(false);
  const [highContrast, setHighContrast] = React.useState(false);
  const isAccessibilityEnabled = useIsFutureEnabled('accessibilityMenu');

  useEffect(() => {
    if (!isAccessibilityEnabled) return;
    // Check user preferences
    const darkModePreference = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(darkModePreference.matches);

    // Listen for changes
    const handler = (e: MediaQueryListEvent) => setIsDarkMode(e.matches);
    darkModePreference.addEventListener('change', handler);

    return () => darkModePreference.removeEventListener('change', handler);
  }, [isAccessibilityEnabled]);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.classList.toggle('high-contrast', highContrast);
  }, [isDarkMode, highContrast]);

  const toggleDarkMode = () => setIsDarkMode(prev => !prev);
  const toggleHighContrast = () => setHighContrast(prev => !prev);

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode, highContrast, toggleHighContrast }}>
      {children}
    </ThemeContext.Provider>
  );
};
